/**
 * Position convention for venue_pages_nav_items rows:
 *   - `is_hidden = false`: `position` is 0..N-1, unique within (venue_page_id),
 *     and is the source of truth for shown-list ordering.
 *   - `is_hidden = true`: `position = -1` (sentinel – see
 *     `packages/venues/src/data/venue_pages_nav_items.ts`).
 *
 * The DEFAULT_* constants below are NOT used for DB-backed positions. They are
 * only referenced when computing a fallback position for entries that have no
 * row yet (e.g. first render before any toggle/reorder has happened).
 *
 * This module is intentionally framework-neutral (no React, no `"use client"`
 * directive) so it can be imported from server components, client components,
 * and server actions alike.
 */

/**
 * Base navigation item keys that can be referenced in the venue_pages_nav_items table.
 * These represent the standard navigation items available on venue pages.
 */
export const BASE_NAV_ITEM_KEYS = {
  HOME: "home",
  CALENDAR: "calendar",
  MAP_PARKING: "map_parking",
  MENU: "menu",
  FAQS: "faqs",
  CONTACT: "contact",
  ABOUT: "about",
  SOCIALS: "socials",
  SUBSCRIBE: "subscribe",
  FREE_SHOWS: "free-shows",
  SHOWS_TONIGHT: "shows-tonight",
} as const;

export type BaseNavItemKey =
  (typeof BASE_NAV_ITEM_KEYS)[keyof typeof BASE_NAV_ITEM_KEYS];

/**
 * Built-in page keys whose path overlaps with a reserved custom_links path.
 * A custom_links row at one of these paths is treated as a content override
 * for the corresponding base item, not as an independent custom page.
 */
export const BUILT_IN_PAGE_KEYS = [
  BASE_NAV_ITEM_KEYS.FREE_SHOWS,
  BASE_NAV_ITEM_KEYS.SHOWS_TONIGHT,
] as const;
export type BuiltInPageKey = (typeof BUILT_IN_PAGE_KEYS)[number];
export const isBuiltInPageKey = (key: string): key is BuiltInPageKey =>
  (BUILT_IN_PAGE_KEYS as readonly string[]).includes(key);

/**
 * Base nav keys that are hidden from the navbar by default and require an
 * explicit `venue_pages_nav_items` opt-in row to appear. The page still
 * exists at its URL regardless. Built-in content-override pages are a
 * superset of this set (they're opt-in too), but a key can be opt-in
 * without having a content override mechanism (e.g. Subscribe).
 */
export const OPT_IN_BASE_NAV_KEYS = [
  BASE_NAV_ITEM_KEYS.FREE_SHOWS,
  BASE_NAV_ITEM_KEYS.SHOWS_TONIGHT,
  BASE_NAV_ITEM_KEYS.SUBSCRIBE,
] as const;
export type OptInBaseNavKey = (typeof OPT_IN_BASE_NAV_KEYS)[number];
export const isOptInBaseNavKey = (key: string): key is OptInBaseNavKey =>
  (OPT_IN_BASE_NAV_KEYS as readonly string[]).includes(key);

/**
 * Default positions for base navigation items.
 * These are used when no overrides exist in the database.
 */
export const DEFAULT_BASE_NAV_POSITIONS: Record<BaseNavItemKey, number> = {
  [BASE_NAV_ITEM_KEYS.HOME]: 0,
  [BASE_NAV_ITEM_KEYS.CALENDAR]: 1,
  [BASE_NAV_ITEM_KEYS.SHOWS_TONIGHT]: 2,
  [BASE_NAV_ITEM_KEYS.FREE_SHOWS]: 3,
  [BASE_NAV_ITEM_KEYS.MAP_PARKING]: 4,
  [BASE_NAV_ITEM_KEYS.MENU]: 5,
  [BASE_NAV_ITEM_KEYS.FAQS]: 6,
  [BASE_NAV_ITEM_KEYS.CONTACT]: 7,
  [BASE_NAV_ITEM_KEYS.ABOUT]: 8,
  [BASE_NAV_ITEM_KEYS.SUBSCRIBE]: 9,
  [BASE_NAV_ITEM_KEYS.SOCIALS]: 10,
};

/**
 * Starting position for custom links when no overrides exist.
 * Custom links will be positioned at 100, 101, 102, etc.
 */
export const DEFAULT_CUSTOM_LINK_START_POSITION = 100;

/**
 * Starting position for external links when no overrides exist.
 * External links will be positioned at 200, 201, 202, etc.
 */
export const DEFAULT_EXTERNAL_LINK_START_POSITION = 200;

/**
 * Configuration for base navigation items including their labels and title attributes.
 */
export const BASE_NAV_ITEM_CONFIG: Record<
  BaseNavItemKey,
  { label: string; title?: string }
> = {
  [BASE_NAV_ITEM_KEYS.HOME]: { label: "Home" },
  [BASE_NAV_ITEM_KEYS.CALENDAR]: { label: "Calendar", title: "Calendar" },
  [BASE_NAV_ITEM_KEYS.MAP_PARKING]: {
    label: "Map & Parking",
    title: "Map, Parking & Directions",
  },
  [BASE_NAV_ITEM_KEYS.MENU]: { label: "Menu", title: "Menu" },
  [BASE_NAV_ITEM_KEYS.FAQS]: {
    label: "FAQs",
    title: "Frequently Asked Questions",
  },
  [BASE_NAV_ITEM_KEYS.CONTACT]: { label: "Contact", title: "Contact Us" },
  [BASE_NAV_ITEM_KEYS.ABOUT]: { label: "About", title: "About Us" },
  [BASE_NAV_ITEM_KEYS.SOCIALS]: { label: "Socials", title: "Socials" },
  [BASE_NAV_ITEM_KEYS.SUBSCRIBE]: {
    label: "Subscribe",
    title: `Subscribe for Updates`,
  },
  [BASE_NAV_ITEM_KEYS.FREE_SHOWS]: { label: "Free Shows", title: "Free Shows" },
  [BASE_NAV_ITEM_KEYS.SHOWS_TONIGHT]: {
    label: "Shows Tonight",
    title: "Shows Tonight",
  },
};
